// Copyright (c) 2026 Anchorbase, Inc. All rights reserved.

import { z } from 'zod';
import {
    MACOS_INSTALLER_PUBLISHED_FILE_NAME,
    type NativeReleaseCatalog,
    type NativeReleaseCatalogV1,
    type NativeReleaseCatalogV2,
    type NativeReleasePackageV1,
    type NativeReleasePackageV2,
    type ReleaseIdentityV1,
} from '../native-release-catalog.types';
import {
    WINDOWS_INSTALLER_PUBLISHED_FILE_NAME,
    WINDOWS_INSTALLER_QA_PUBLISHED_FILE_NAME,
} from '../windows-artifact-qa.types';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../stored-json-contract.types';

const Sha256Schema: z.ZodType<string> = z.string().regex(/^[0-9a-f]{64}$/);
const CommitIdSchema: z.ZodType<string> = z.string().regex(/^[0-9a-f]{40}$/);

export const BuildReleaseIdentityV1Schema = z
    .object({
        identityKind: z.literal('build'),
        buildNumber: z.string().regex(/^[1-9][0-9]*$/),
    })
    .strict();

export const VersionReleaseIdentityV1Schema = z
    .object({
        identityKind: z.literal('release'),
        releaseVersion: z.string().regex(/^[0-9]+\.[0-9]+\.[0-9]+$/),
    })
    .strict();

export const ReleaseIdentityV1Schema: z.ZodType<ReleaseIdentityV1> = z.discriminatedUnion(
    'identityKind',
    [BuildReleaseIdentityV1Schema, VersionReleaseIdentityV1Schema]
);

const NativeReleasePackageBaseV1Schema = {
    objectKey: z.string().min(1),
    sha256: Sha256Schema,
};

const WindowsReleasePackageBaseV1Schema = {
    ...NativeReleasePackageBaseV1Schema,
    platform: z.literal('win'),
    architecture: z.enum(['x64', 'arm64']),
    track: z.enum(['stable', 'latest']),
    pipeline: z.literal('stable'),
    imageLabel: z.string().min(1),
    displayLabel: z.string().min(1),
};

export const MacArm64DmgPackageV1Schema = z
    .object({
        ...NativeReleasePackageBaseV1Schema,
        packageKind: z.literal('mac_arm64_dmg'),
        platform: z.literal('mac'),
        architecture: z.literal('arm64'),
        packageType: z.literal('dmg'),
        publicFileName: z.literal(MACOS_INSTALLER_PUBLISHED_FILE_NAME),
    })
    .strict();

export const MacX64DmgPackageV2Schema = z
    .object({
        ...NativeReleasePackageBaseV1Schema,
        packageKind: z.literal('mac_x64_dmg'),
        platform: z.literal('mac'),
        architecture: z.literal('x64'),
        packageType: z.literal('dmg'),
        publicFileName: z.literal(MACOS_INSTALLER_PUBLISHED_FILE_NAME),
    })
    .strict();

export const WindowsPortablePackageV1Schema = z
    .object({
        ...WindowsReleasePackageBaseV1Schema,
        packageKind: z.literal('windows_portable'),
        packageType: z.literal('portable'),
        publicFileName: z.string().min(1),
    })
    .strict();

export const WindowsInstallerPackageV1Schema = z
    .object({
        ...WindowsReleasePackageBaseV1Schema,
        packageKind: z.literal('windows_installer'),
        packageType: z.literal('installer'),
        publicFileName: z.literal(WINDOWS_INSTALLER_PUBLISHED_FILE_NAME),
        qa: z
            .object({
                objectKey: z.string().min(1),
                sha256: Sha256Schema,
                publicFileName: z.literal(WINDOWS_INSTALLER_QA_PUBLISHED_FILE_NAME),
            })
            .strict(),
    })
    .strict();

export const NativeReleasePackageV1Schema: z.ZodType<NativeReleasePackageV1> = z.discriminatedUnion(
    'packageKind',
    [MacArm64DmgPackageV1Schema, WindowsPortablePackageV1Schema, WindowsInstallerPackageV1Schema]
);

export const NativeReleasePackageV2Schema: z.ZodType<NativeReleasePackageV2> = z.discriminatedUnion(
    'packageKind',
    [
        MacArm64DmgPackageV1Schema,
        MacX64DmgPackageV2Schema,
        WindowsPortablePackageV1Schema,
        WindowsInstallerPackageV1Schema,
    ]
);

function packageCoordinate(packageValue: NativeReleasePackageV2): string {
    switch (packageValue.packageKind) {
        case 'mac_arm64_dmg':
        case 'mac_x64_dmg':
            return packageValue.packageKind;
        case 'windows_portable':
        case 'windows_installer':
            return [packageValue.track, packageValue.architecture, packageValue.packageType].join(
                ':'
            );
    }
}

const requiredPackageCoordinatesV1: readonly string[] = [
    'mac_arm64_dmg',
    'stable:x64:portable',
    'stable:x64:installer',
    'stable:arm64:portable',
    'stable:arm64:installer',
    'latest:x64:portable',
    'latest:x64:installer',
    'latest:arm64:portable',
    'latest:arm64:installer',
];

const requiredPackageCoordinatesV2: readonly string[] = [
    'mac_x64_dmg',
    ...requiredPackageCoordinatesV1,
];

function releaseIdentityMatchesTag(identity: ReleaseIdentityV1, tag: string): boolean {
    switch (identity.identityKind) {
        case 'build': {
            return (
                /^build-[0-9]{6}$/.test(tag) &&
                String(Number.parseInt(tag.slice('build-'.length), 10)) === identity.buildNumber
            );
        }
        case 'release':
            return tag === `v${identity.releaseVersion}`;
    }
}

function artifactRootPrefix(artifactTag: string): string {
    return artifactTag.startsWith('v')
        ? `releases/${artifactTag.slice(1)}/`
        : `builds/${artifactTag}/`;
}

export const NativeReleaseCatalogV1Schema: z.ZodType<NativeReleaseCatalogV1> = z
    .object({
        schemaVersion: z.literal(1),
        portal: z
            .object({
                identity: ReleaseIdentityV1Schema,
                imageTag: z.string().regex(/^(build-[0-9]{6}|v[0-9]+\.[0-9]+\.[0-9]+)$/),
                sourceCommit: CommitIdSchema,
            })
            .strict(),
        native: z
            .object({
                identity: ReleaseIdentityV1Schema,
                artifactTag: z.string().regex(/^(build-[0-9]{6}|v[0-9]+\.[0-9]+\.[0-9]+)$/),
                sourceGitTag: z.string().regex(/^(build-[0-9]{6}|v[0-9]+\.[0-9]+\.[0-9]+)$/),
                sourceCommit: CommitIdSchema,
                nativeInputManifestSha256: Sha256Schema,
                latestNativeInputCommit: CommitIdSchema,
                nativeFingerprint: z.string().min(1),
            })
            .strict(),
        packages: z.array(NativeReleasePackageV1Schema).length(requiredPackageCoordinatesV1.length),
    })
    .strict()
    .superRefine((catalog, context) => {
        const packageCounts = new Map<string, number>();
        for (const packageValue of catalog.packages) {
            const coordinate = packageCoordinate(packageValue);
            packageCounts.set(coordinate, (packageCounts.get(coordinate) ?? 0) + 1);
        }
        for (const coordinate of requiredPackageCoordinatesV1) {
            if (packageCounts.get(coordinate) !== 1) {
                context.addIssue({
                    code: 'custom',
                    message: `Native release catalog must contain exactly one ${coordinate} package.`,
                    path: ['packages'],
                });
            }
        }
        if (!releaseIdentityMatchesTag(catalog.portal.identity, catalog.portal.imageTag)) {
            context.addIssue({
                code: 'custom',
                message: 'Portal release identity does not match its image tag.',
                path: ['portal', 'identity'],
            });
        }
        if (!releaseIdentityMatchesTag(catalog.native.identity, catalog.native.artifactTag)) {
            context.addIssue({
                code: 'custom',
                message: 'Native release identity does not match its artifact tag.',
                path: ['native', 'identity'],
            });
        }
        const requiredObjectPrefix = artifactRootPrefix(catalog.native.artifactTag);
        for (const [index, packageValue] of catalog.packages.entries()) {
            const requiredPackageDirectory =
                packageValue.packageKind === 'mac_arm64_dmg'
                    ? `${requiredObjectPrefix}macos/stable/arm64/dmg/`
                    : `${requiredObjectPrefix}windows/${packageValue.track}/${packageValue.architecture}/${packageValue.packageType}/`;
            if (
                packageValue.objectKey !==
                `${requiredPackageDirectory}${packageValue.publicFileName}`
            ) {
                context.addIssue({
                    code: 'custom',
                    message: 'Package object key does not match its catalog coordinate.',
                    path: ['packages', index, 'objectKey'],
                });
            }
            if (
                packageValue.packageKind === 'windows_installer' &&
                packageValue.qa.objectKey !==
                    `${requiredPackageDirectory}${packageValue.qa.publicFileName}`
            ) {
                context.addIssue({
                    code: 'custom',
                    message: 'Installer QA object key is not adjacent to its installer.',
                    path: ['packages', index, 'qa', 'objectKey'],
                });
            }
        }
    });

export const NativeReleaseCatalogV2Schema: z.ZodType<NativeReleaseCatalogV2> = z
    .object({
        schemaVersion: z.literal(2),
        portal: z
            .object({
                identity: ReleaseIdentityV1Schema,
                imageTag: z.string().regex(/^(build-[0-9]{6}|v[0-9]+\.[0-9]+\.[0-9]+)$/),
                sourceCommit: CommitIdSchema,
            })
            .strict(),
        native: z
            .object({
                identity: ReleaseIdentityV1Schema,
                artifactTag: z.string().regex(/^(build-[0-9]{6}|v[0-9]+\.[0-9]+\.[0-9]+)$/),
                sourceGitTag: z.string().regex(/^(build-[0-9]{6}|v[0-9]+\.[0-9]+\.[0-9]+)$/),
                sourceCommit: CommitIdSchema,
                nativeInputManifestSha256: Sha256Schema,
                latestNativeInputCommit: CommitIdSchema,
                nativeFingerprint: z.string().min(1),
            })
            .strict(),
        packages: z.array(NativeReleasePackageV2Schema).length(requiredPackageCoordinatesV2.length),
    })
    .strict()
    .superRefine((catalog, context) => {
        const packageCounts = new Map<string, number>();
        for (const packageValue of catalog.packages) {
            const coordinate = packageCoordinate(packageValue);
            packageCounts.set(coordinate, (packageCounts.get(coordinate) ?? 0) + 1);
        }
        for (const coordinate of requiredPackageCoordinatesV2) {
            if (packageCounts.get(coordinate) !== 1) {
                context.addIssue({
                    code: 'custom',
                    message: `Native release catalog must contain exactly one ${coordinate} package.`,
                    path: ['packages'],
                });
            }
        }
        if (!releaseIdentityMatchesTag(catalog.portal.identity, catalog.portal.imageTag)) {
            context.addIssue({
                code: 'custom',
                message: 'Portal release identity does not match its image tag.',
                path: ['portal', 'identity'],
            });
        }
        if (!releaseIdentityMatchesTag(catalog.native.identity, catalog.native.artifactTag)) {
            context.addIssue({
                code: 'custom',
                message: 'Native release identity does not match its artifact tag.',
                path: ['native', 'identity'],
            });
        }
        const requiredObjectPrefix = artifactRootPrefix(catalog.native.artifactTag);
        for (const [index, packageValue] of catalog.packages.entries()) {
            const requiredPackageDirectory =
                packageValue.platform === 'mac'
                    ? `${requiredObjectPrefix}macos/stable/${packageValue.architecture}/dmg/`
                    : `${requiredObjectPrefix}windows/${packageValue.track}/${packageValue.architecture}/${packageValue.packageType}/`;
            if (
                packageValue.objectKey !==
                `${requiredPackageDirectory}${packageValue.publicFileName}`
            ) {
                context.addIssue({
                    code: 'custom',
                    message: 'Package object key does not match its catalog coordinate.',
                    path: ['packages', index, 'objectKey'],
                });
            }
            if (
                packageValue.packageKind === 'windows_installer' &&
                packageValue.qa.objectKey !==
                    `${requiredPackageDirectory}${packageValue.qa.publicFileName}`
            ) {
                context.addIssue({
                    code: 'custom',
                    message: 'Installer QA object key is not adjacent to its installer.',
                    path: ['packages', index, 'qa', 'objectKey'],
                });
            }
        }
    });

export const NativeReleaseCatalogContract = defineStoredJsonContract({
    contractName: 'NativeReleaseCatalog',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'NativeReleaseCatalogV1Schema',
            schema: NativeReleaseCatalogV1Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['schemaVersion'],
                    markerValue: 1,
                },
            ],
            normalize: (value: NativeReleaseCatalogV1): NativeReleaseCatalog => ({
                ...value,
                schemaVersion: 2,
            }),
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'NativeReleaseCatalogV2Schema',
            schema: NativeReleaseCatalogV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['schemaVersion'],
                    markerValue: 2,
                },
            ],
            normalize: (value: NativeReleaseCatalogV2): NativeReleaseCatalog => value,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const NativeReleaseCatalogSchema: z.ZodType<NativeReleaseCatalog> =
    buildStoredJsonValueParser(NativeReleaseCatalogContract);

export function serializeNativeReleaseCatalogV2(
    value: NativeReleaseCatalog
): NativeReleaseCatalogV2 {
    return NativeReleaseCatalogV2Schema.parse(value);
}
